// components/common/PhoneInput.tsx
import React from 'react';
import PhoneInputBase, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import '../../styles/phone-input.css'; // Import the CSS

export const DEFAULT_COUNTRY = 'TT';
export const DEFAULT_COUNTRY_CODE = '+1868';

interface PhoneInputProps {
    value: string;
    onChange: (value: string) => void;
    label: string;
    required?: boolean;
    error?: string;
    name: string;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
    value,
    onChange,
    label,
    required = false,
    error,
    name,
    disabled = false,
    className = '',
    placeholder = 'Enter phone number',
}) => {
    return (
        <div className={`mb-4 ${className}`}>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>

            <div className={`PhoneInput ${error ? 'PhoneInput--error' : ''}`}>
                <PhoneInputBase
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry={DEFAULT_COUNTRY}
                    countries={[
                        'TT', 'JM', 'BB', 'GY', 'VC', 'GD', 'KN', 'LC', 'AG', 'DM',
                        'US', 'CA', 'GB', 'AU', 'IN', 'PK', 'BD', 'LK', 'CN', 'JP'
                    ]}
                    value={value}
                    onChange={(val) => {
                        // Force E.164 format
                        if (val) {
                            // Remove formatting
                            const clean = val.replace(/[^\d+]/g, '');
                            onChange(clean);
                        } else {
                            onChange('');
                        }
                    }}
                    disabled={disabled}
                    id={name}
                    placeholder={placeholder}
                    className="w-full"
                />
            </div>

            {error && (
                <div className="mt-1 text-sm text-red-600">{error}</div>
            )}

            {value && !error && (
                <div className="mt-1 text-xs text-gray-500">
                    {formatPhoneDisplay(value)}
                </div>
            )}
        </div>
    );
};

// Helper function to format phone for display
const formatPhoneDisplay = (phone: string): string => {
    const countryNames: Record<string, string> = {
        '+1868': 'Trinidad & Tobago',
        '+1876': 'Jamaica',
        '+1246': 'Barbados',
        '+1242': 'Bahamas',
        '+592': 'Guyana',
        '+1767': 'Dominica',
        '+1809': 'Dominican Republic',
        '+1473': 'Grenada',
        '+1671': 'Guam',
        '+509': 'Haiti',
        '+596': 'Martinique',
        '+1664': 'Montserrat',
        '+1670': 'Northern Mariana Islands',
        '+1787': 'Puerto Rico',
        '+1869': 'Saint Kitts and Nevis',
        '+1758': 'Saint Lucia',
        '+1784': 'Saint Vincent and the Grenadines',
        '+1721': 'Sint Maarten',
        '+1649': 'Turks and Caicos Islands',
        '+1340': 'US Virgin Islands',
        '+1284': 'British Virgin Islands',
    };

    // Sort by code length (longest first) to avoid partial matches
    const sortedCodes = Object.keys(countryNames).sort((a, b) => b.length - a.length);

    for (const code of sortedCodes) {
        if (phone.startsWith(code)) {
            return countryNames[code];
        }
    }

    // Check for US/Canada (+1) only if not matched above
    if (phone.startsWith('+1')) {
        // Try to identify specific area codes
        const areaCode = phone.substring(2, 5);
        const usCanadaAreas: Record<string, string> = {
            '868': 'Trinidad & Tobago', // This should already be caught above
            '876': 'Jamaica',
            '246': 'Barbados',
            // US/Canada area codes
            '202': 'Washington DC, US',
            '212': 'New York City, US',
            '310': 'Los Angeles, US',
            '416': 'Toronto, CA',
            '514': 'Montreal, CA',
            // Add more as needed
        };

        if (areaCode in usCanadaAreas) {
            return usCanadaAreas[areaCode];
        }

        return 'United States/Canada';
    }

    return 'International number';
};

// Export validation helper
export const validatePhoneNumber = (phone: string, isRequired: boolean): string => {
    // Required check
    if (isRequired && !phone.trim()) {
        return 'Phone number is required';
    }

    // Optional and empty - valid
    if (!isRequired && !phone.trim()) {
        return '';
    }

    // Has value, validate
    if (phone.trim()) {
        try {
            // Parse phone number
            const phoneNumber = parsePhoneNumber(phone);

            if (!phoneNumber) {
                return 'Invalid phone number';
            }

            // Check if valid
            if (!isValidPhoneNumber(phoneNumber.number)) {
                return 'Invalid phone number';
            }

            // SPECIAL HANDLING FOR +1 COUNTRIES
            if (phoneNumber.countryCallingCode === '1') {
                // Check if it's a Trinidad & Tobago number (area code 868)
                const fullNumber = phoneNumber.number.replace(/\D/g, '');

                // Remove country code
                const withoutCountryCode = fullNumber.substring(1);

                // Check area code
                if (withoutCountryCode.startsWith('868')) {
                    // Trinidad & Tobago: 7 digits after area code
                    const localNumber = withoutCountryCode.substring(3); // Remove 868

                    if (localNumber.length !== 7) {
                        return `Trinidad & Tobago numbers must be 7 digits after 868 area code. You entered ${localNumber.length} digit${localNumber.length !== 1 ? 's' : ''}.`;
                    }

                    // Validate first digit
                    const firstDigit = localNumber.charAt(0);
                    if (!['2', '3', '4', '6', '7', '8'].includes(firstDigit)) {
                        return 'Trinidad & Tobago numbers must start with 2, 3, 4, 6, 7, or 8';
                    }
                }
                // Other +1 countries (US, Canada, etc.)
                else {
                    // US/Canada: 10 digits total (including area code)
                    if (withoutCountryCode.length !== 10) {
                        return `US/Canada numbers must be 10 digits. You entered ${withoutCountryCode.length} digit${withoutCountryCode.length !== 1 ? 's' : ''}.`;
                    }
                }
            }

            // Other countries validation
            else {
                const nationalNumber = phoneNumber.nationalNumber.replace(/\D/g, '');

                // Add country-specific validations as needed
                if (phoneNumber.country === 'GB') { // UK
                    if (nationalNumber.length < 9 || nationalNumber.length > 10) {
                        return 'UK numbers must be 9-10 digits';
                    }
                }
                // Add more countries...
            }

        } catch (error) {
            console.error('Phone validation error:', error);
            return 'Invalid phone number format';
        }
    }

    return ''; // Valid
};